import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Trash2, CheckCircle2, AlertTriangle, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

import { useTranslation } from "react-i18next";
const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export default function AccountDeletion() {
  const { t } = useTranslation();
  const [identifier, setIdentifier] = useState("");
  const [reason, setReason] = useState("");
  const [ack, setAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!identifier.trim() || identifier.trim().length < 3) {
      toast.error(t("legal.accountdeletion.please_enter_your_registered_phone_numbe"));
      return;
    }
    if (!ack) {
      toast.error(t("legal.accountdeletion.please_confirm_you_understand_what_will"));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/account/deletion-request`, {
        identifier: identifier.trim(),
        reason: reason.trim() || null,
        confirm_ack: true,
      });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Please email support@hisabx.in.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="account-deletion-page">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-6 py-4">
          <Link to="/" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="deletion-back">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
          <h1 className="ml-auto text-lg font-bold">{t("legal.accountdeletion.delete_your_account_data")}</h1>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-8">
        {result ? (
          <SubmittedCard result={result} />
        ) : (
          <>
            <div className="mb-6 space-y-2">
              <p className="text-sm text-muted-foreground">
                You can permanently delete your HisabX account and personal data
                by submitting the form below. Requests are verified and executed
                by our support team within <b>7 business days</b>.
              </p>
            </div>

            <section className="mb-8 space-y-4 rounded-lg border border-border bg-card p-5">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
                  <CheckCircle2 className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-foreground">{t("legal.accountdeletion.what_we_delete")}</h3>
                  <ul className="mt-1 list-inside list-disc space-y-0.5 text-sm text-muted-foreground">
                    <li>{t("legal.accountdeletion.your_name_phone_email_and_profile_photo")}</li>
                    <li>{t("legal.accountdeletion.your_login_sessions_and_refresh_tokens_i")}</li>
                    <li>{t("legal.accountdeletion.push_notification_token_telegram_chat_id")}</li>
                    <li>{t("legal.accountdeletion.selfies_uploaded_during_shift_check_in")}</li>
                    <li>{t("legal.accountdeletion.any_private_messages_or_notes_attached_t")}</li>
                  </ul>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200">
                  <AlertTriangle className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-foreground">{t("legal.accountdeletion.what_we_retain_and_why")}</h3>
                  <ul className="mt-1 list-inside list-disc space-y-0.5 text-sm text-muted-foreground">
                    <li>
                      <b>{t("legal.accountdeletion.business_transaction_records")}</b> — HISAB entries, cashbook,
                      credit statements. Indian GST & Income Tax law requires us
                      to keep these for <b>8 years</b>. Your name is removed from
                      them and replaced with an anonymous internal ID.
                    </li>
                    <li>
                      <b>{t("legal.accountdeletion.audit_logs")}</b> — required for your dealer&apos;s CA (Chartered
                      Accountant) reports and Tally exports.
                    </li>
                    <li>
                      <b>{t("legal.accountdeletion.aggregated_analytics")}</b> — anonymous counts (e.g.
                      &quot;total shifts closed this month&quot;) with no personal identifiers.
                    </li>
                  </ul>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200">
                  <Trash2 className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-foreground">{t("legal.accountdeletion.retention_period")}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Personal data is deleted within 7 business days of verification.
                    Retained financial records are automatically purged 8 years
                    after your account deletion date.
                  </p>
                </div>
              </div>
            </section>

            <section className="rounded-lg border border-border bg-card p-5">
              <h3 className="mb-4 text-sm font-bold text-foreground">{t("legal.accountdeletion.submit_deletion_request")}</h3>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="del-identifier" className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Registered phone number or email *
                  </Label>
                  <Input
                    id="del-identifier"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    placeholder="e.g. 9876543210 or you@example.com"
                    data-testid="deletion-identifier"
                    className="h-11"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="del-reason" className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Reason (optional — helps us improve)
                  </Label>
                  <Textarea
                    id="del-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Switched to a different app"
                    rows={3}
                    data-testid="deletion-reason"
                  />
                </div>
                <label className="flex items-start gap-2.5 rounded-md border border-border bg-secondary/40 p-3 cursor-pointer">
                  <Checkbox
                    checked={ack}
                    onCheckedChange={(v) => setAck(!!v)}
                    data-testid="deletion-ack"
                    className="mt-0.5"
                  />
                  <span className="text-xs text-foreground">
                    I understand that my personal data will be permanently deleted,
                    my business transaction records will be retained anonymously for
                    tax compliance, and this action cannot be undone.
                  </span>
                </label>
                <Button
                  onClick={submit}
                  disabled={submitting || !ack || !identifier.trim()}
                  className="h-12 w-full text-base font-bold"
                  variant="destructive"
                  data-testid="deletion-submit"
                >
                  {submitting ? "Submitting…" : "Submit deletion request"}
                </Button>
              </div>
            </section>

            <section className="mt-6 rounded-lg border border-border bg-secondary/30 p-4">
              <h3 className="mb-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                Alternative: email us
              </h3>
              <p className="text-sm text-foreground">
                Prefer email? Send your request to
                {" "}
                <button
                  type="button"
                  onClick={() => { navigator.clipboard.writeText("support@hisabx.in"); toast.success(t("legal.accountdeletion.email_copied")); }}
                  className="inline-flex items-center gap-1 font-mono font-semibold text-primary hover:underline"
                  data-testid="copy-support-email"
                >
                  support@hisabx.in <Copy className="h-3 w-3" />
                </button>
                {" "}with subject line <i>&quot;Delete my HisabX account&quot;</i> from
                the email address (or phone number) on your account. We&apos;ll respond
                within 2 business days.
              </p>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function SubmittedCard({ result }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-6 dark:border-emerald-900/60 dark:bg-emerald-950/40" data-testid="deletion-submitted">
      <div className="mb-4 flex items-center gap-3">
        <CheckCircle2 className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
        <div>
          <h2 className="text-lg font-bold text-emerald-900 dark:text-emerald-100">{t("legal.accountdeletion.request_received")}</h2>
          <p className="text-sm text-emerald-800 dark:text-emerald-200">{t("legal.accountdeletion.your_deletion_request_has_been_submitted")}</p>
        </div>
      </div>
      <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">{t("legal.accountdeletion.request_id")}</dt>
          <dd className="mt-1 font-mono text-emerald-950 dark:text-emerald-50">{result.request_id}</dd>
        </div>
        <div>
          <dt className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">{t("legal.accountdeletion.submitted_at")}</dt>
          <dd className="mt-1 text-emerald-950 dark:text-emerald-50">{new Date(result.submitted_at).toLocaleString()}</dd>
        </div>
        <div>
          <dt className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">{t("dealer.status")}</dt>
          <dd className="mt-1 text-emerald-950 dark:text-emerald-50">{result.status}</dd>
        </div>
        <div>
          <dt className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">{t("legal.accountdeletion.support_email")}</dt>
          <dd className="mt-1 font-mono text-emerald-950 dark:text-emerald-50">{result.contact_email}</dd>
        </div>
      </dl>
      <p className="mt-4 text-xs text-emerald-800 dark:text-emerald-200">
        Save the Request ID above. Our support team will verify your identity and
        execute the deletion within 7 business days. You&apos;ll receive an email
        confirmation once complete.
      </p>
      <Link to="/" className="mt-4 inline-block text-sm font-semibold text-emerald-800 underline dark:text-emerald-200" data-testid="deletion-home">
        ← Back to home
      </Link>
    </div>
  );
}
